import logger from '@/utils/client-logger';
import { getSetLocalStorage } from '@/utils/tools';
import type { SnowplowPixelPlatform } from './snowplowPixelMapping';

export const AD_CLICK_ATTRIBUTION_TTL_DAYS = 400;
export const AD_CLICK_ATTRIBUTION_TTL_MS = AD_CLICK_ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;
export const AD_CLICK_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS =
  AD_CLICK_ATTRIBUTION_TTL_DAYS * 24 * 60 * 60;

export interface AdClickTransformInput {
  provider: SnowplowPixelPlatform;
  queryKey: string;
  storageKey: string;
  value: string;
  now: number;
}

export type AdClickValueTransform = (input: AdClickTransformInput) => string | null | undefined;

export interface AdClickAttributionConfig {
  provider: SnowplowPixelPlatform;
  queryKeys: readonly string[];
  storageKey: string;
  transform?: AdClickValueTransform;
}

export interface AdClickAttributionRecord {
  provider: SnowplowPixelPlatform;
  queryKey: string;
  storageKey: string;
  value: string;
  expireAt: number;
}

export interface CreateAdClickAttributionRecordsOptions {
  config?: readonly AdClickAttributionConfig[];
  now?: () => number;
}

export interface CaptureAdClickAttributionOptions extends CreateAdClickAttributionRecordsOptions {
  search?: string;
}

export function transformFacebookFbc({ value, now }: AdClickTransformInput): string {
  return `fb.1.${now}.${value}`;
}

export const AD_CLICK_ATTRIBUTION_CONFIG: readonly AdClickAttributionConfig[] = [
  {
    provider: 'facebook',
    queryKeys: ['fbclid'],
    storageKey: 'z_fb_fbc',
    transform: transformFacebookFbc,
  },
  {
    provider: 'tiktok',
    queryKeys: ['ttclid'],
    storageKey: 'z_tt_ttclid',
  },
  {
    provider: 'bing',
    queryKeys: ['msclkid'],
    storageKey: 'z_ms_uetmclkid',
  },
  {
    provider: 'snapchat',
    queryKeys: ['ScCid', 'sccid'],
    storageKey: 'z_sp_scid',
  },
  {
    provider: 'rtbHouse',
    queryKeys: ['rtbhc'],
    storageKey: 'z_rtb_rtbhc',
  },
  {
    provider: 'criteo',
    queryKeys: ['cto_pld'],
    storageKey: 'z_cto_cto_pld',
  },
];

function getFirstQueryValue(
  queryParams: URLSearchParams,
  queryKeys: readonly string[],
): { queryKey: string; value: string } | null {
  for (const queryKey of queryKeys) {
    const value = queryParams.getAll(queryKey).find((item) => item.trim());
    if (value) {
      return { queryKey, value };
    }
  }
  return null;
}

function resolveRecordValue(
  config: AdClickAttributionConfig,
  queryKey: string,
  value: string,
  now: number,
): string | null {
  try {
    const transformedValue = config.transform
      ? config.transform({
          provider: config.provider,
          queryKey,
          storageKey: config.storageKey,
          value,
          now,
        })
      : value;

    if (!transformedValue || !transformedValue.trim()) {
      return null;
    }

    return transformedValue;
  } catch (error) {
    logger.error('Ad click attribution transform error:', {
      provider: config.provider,
      queryKey,
      error,
    });
    return null;
  }
}

export function createAdClickAttributionRecords(
  search: string,
  options: CreateAdClickAttributionRecordsOptions = {},
): AdClickAttributionRecord[] {
  const configList = options.config ?? AD_CLICK_ATTRIBUTION_CONFIG;
  const now = options.now?.() ?? Date.now();
  const expireAt = now + AD_CLICK_ATTRIBUTION_TTL_MS;
  const queryParams = new URLSearchParams(search);
  const records: AdClickAttributionRecord[] = [];

  for (const config of configList) {
    const matchedQuery = getFirstQueryValue(queryParams, config.queryKeys);
    if (!matchedQuery) continue;

    const transformedValue = resolveRecordValue(
      config,
      matchedQuery.queryKey,
      matchedQuery.value,
      now,
    );
    if (!transformedValue) continue;

    records.push({
      provider: config.provider,
      queryKey: matchedQuery.queryKey,
      storageKey: config.storageKey,
      value: transformedValue,
      expireAt,
    });
  }

  return records;
}

export function buildAdClickCookieString(storageKey: string, value: string): string {
  return `${storageKey}=${encodeURIComponent(value)}; Max-Age=${AD_CLICK_ATTRIBUTION_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax`;
}

function persistAdClickAttributionRecord(record: AdClickAttributionRecord): void {
  getSetLocalStorage.set(record.storageKey, record.value, record.expireAt);
  document.cookie = buildAdClickCookieString(record.storageKey, record.value);
}

export function captureAdClickAttribution(
  options: CaptureAdClickAttributionOptions = {},
): AdClickAttributionRecord[] {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return [];
  }

  try {
    const records = createAdClickAttributionRecords(
      options.search ?? window.location.search,
      options,
    );

    records.forEach((record) => {
      try {
        persistAdClickAttributionRecord(record);
      } catch (error) {
        logger.error('Ad click attribution persist error:', {
          provider: record.provider,
          storageKey: record.storageKey,
          error,
        });
      }
    });

    return records;
  } catch (error) {
    logger.error('Ad click attribution capture error:', error);
    return [];
  }
}
